import React, { useRef, useEffect, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const TurnstileWidget = ({ 
  onSuccess, 
  onError, 
  onExpire,
  size = 'normal',
  theme = 'auto',
  retry = 'auto',
  refreshExpired = 'auto',
  disabled = false,
  className = ''
}) => {
  const turnstileRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState('');
  const [devTokenSent, setDevTokenSent] = useState(false);
  
  // TURNSTILE COMPLETELY DISABLED - Force no site key
  const siteKey = null; // process.env.REACT_APP_TURNSTILE_SITE_KEY;
  
  // Immediately provide dev token if in development and no site key
  if (!siteKey && process.env.NODE_ENV === 'development' && !devTokenSent && onSuccess) {
    console.log('🔓 Development mode: Auto-providing mock Turnstile token immediately');
    setDevTokenSent(true);
    setTimeout(() => onSuccess('dev-mock-token-immediate'), 0);
  }
  
  useEffect(() => {
    if (!siteKey && process.env.NODE_ENV === 'development') {
      console.warn('Turnstile site key not configured. Set REACT_APP_TURNSTILE_SITE_KEY.');
      console.log('Development mode: Auto-generating mock Turnstile token immediately');
      setError('');
      // Immediately call onSuccess with mock token
      if (onSuccess) {
        onSuccess('dev-mock-token-' + Date.now());
      }
    } else if (!siteKey) {
      console.warn('Turnstile site key not configured. Set REACT_APP_TURNSTILE_SITE_KEY.');
      setError('Captcha service not configured');
    }
  }, [siteKey, onSuccess]);

  const handleSuccess = (token) => {
    console.log('Turnstile verification successful');
    setError('');
    if (onSuccess) {
      onSuccess(token);
    }
  };

  const handleError = (error) => {
    console.error('Turnstile verification error:', error);
    setError('Captcha verification failed. Please try again.');
    if (onError) {
      onError(error);
    }
  };

  const handleExpire = () => {
    console.log('Turnstile token expired');
    setError('Captcha expired. Please verify again.');
    if (onExpire) {
      onExpire();
    }
  };

  const handleLoad = () => {
    console.log('Turnstile widget loaded');
    setIsLoaded(true);
    setError('');
  };

  // Don't render if no site key (development mode fallback)
  if (!siteKey) {
    return (
      <div className={`turnstile-placeholder ${className}`} style={{
        padding: '1rem',
        border: '2px dashed #E5E7EB',
        borderRadius: '8px',
        textAlign: 'center',
        color: '#6B7280',
        fontSize: '0.875rem',
        backgroundColor: '#F9FAFB'
      }}>
        <div>🚫 Captcha Disabled</div>
        <div style={{ fontSize: '0.75rem', marginTop: '0.5rem' }}>
          Turnstile verification temporarily disabled
        </div>
        <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: '#059669' }}>
          ✓ Auto-verified (no captcha required)
        </div>
      </div>
    );
  }

  return (
    <div className={`turnstile-container ${className}`}>
      <Turnstile
        ref={turnstileRef}
        siteKey={siteKey}
        onSuccess={handleSuccess}
        onError={handleError}
        onExpire={handleExpire}
        onLoad={handleLoad}
        options={{
          size,
          theme,
          retry,
          'refresh-expired': refreshExpired,
          'response-field-name': 'turnstileToken'
        }}
        style={{
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto'
        }}
      />
      
      {error && (
        <div style={{
          marginTop: '0.5rem',
          padding: '0.5rem',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          color: '#B91C1C',
          fontSize: '0.875rem'
        }}>
          {error}
        </div>
      )}
      
      {!isLoaded && !error && (
        <div style={{
          marginTop: '0.5rem',
          color: '#6B7280',
          fontSize: '0.875rem'
        }}>
          Loading security verification...
        </div>
      )}
    </div>
  );
};

export default TurnstileWidget;